import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../SharedControlPanel.scss';
import './StreamDestinationsPanel.scss';
import useProject from '../../../../../hooks/useProject';
import TickIcon from '../../../../../images/tick-destination-icon.png';
import { StreamDestinationModal } from '../../../../Modals/StreamDestinationModal/StreamDestinationModal';
import { DefaultDestination, Destination } from '../../../../../types/Destination';

type StreamDestinationsPanelProps = {
  onUpdate: () => void;
};

const StreamDestinationsPanel = ({ onUpdate }: StreamDestinationsPanelProps) => {
  const { t } = useTranslation();
  const { project } = useProject();
  const destinations = project?.stream?.encoders?.flatMap((e) => e.destinations || []) ?? [];
  const maxNumberOfDestinations = 10;
  const defaultDestination = DefaultDestination;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState<Destination>({
    ...defaultDestination,
    languageIsoCode: project?.stream?.sourceLanguageIsoCode || 'en-us',
  });

  const openDestinationModal = (
    key = '',
    url = '',
    id = '',
    languageIsoCode = project?.stream?.sourceLanguageIsoCode || 'en-us',
    name = '',
  ) => {
    setSelectedDestination({
      ...defaultDestination,
      id: id,
      encoderId: project?.encoder?.id || '',
      streamKey: key,
      streamUrl: url,
      languageIsoCode: languageIsoCode,
      name: name,
    });
    setIsModalOpen(true);
  };

  const createAddDestinationButtons = () => {
    const addDestinationButtons = [];

    for (let index = 0; index < maxNumberOfDestinations - (destinations.length ?? 0); index++) {
      addDestinationButtons.push(
        <button
          key={index}
          className="control-panel-section-box control-panel-section-box-new"
          onClick={() => openDestinationModal()}
        >
          <span className="control-panel-section-box-text">
            + {t('producer.control-panel.stream-destinations.add-new')}
          </span>
        </button>,
      );
    }

    if ((destinations.length ?? 0) >= maxNumberOfDestinations) {
      addDestinationButtons.push(
        <button
          key={destinations.length ?? 0}
          className="control-panel-section-box control-panel-section-box-new"
          onClick={() => openDestinationModal()}
        >
          <span className="control-panel-section-box-text">
            + {t('producer.control-panel.stream-destinations.add-new')}
          </span>
        </button>,
      );
    }

    return addDestinationButtons;
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    onUpdate();
  };

  return (
    <div className="stream-source-panel">
      <div className="stream-source-panel-section">
        <div className="stream-source-panel-section-content">
          {destinations &&
            destinations.length > 0 &&
            destinations.map((destination: Destination, index: number) => (
              <button
                key={index}
                className="control-panel-section-box"
                onClick={() =>
                  openDestinationModal(
                    destination?.streamKey,
                    destination?.streamUrl,
                    destination?.id,
                    destination?.languageIsoCode,
                    destination?.name,
                  )
                }
              >
                <span className="control-panel-section-box-text">{destination?.name}</span>
                <img src={TickIcon} alt="tick" className="control-panel-section-box-tick" />
              </button>
            ))}
          {createAddDestinationButtons()}
        </div>
      </div>

      {isModalOpen && (
        <StreamDestinationModal
          isOpen={isModalOpen}
          contentLabel="Stream Destination Modal"
          selectedDestination={selectedDestination}
          handleCloseModal={handleCloseModal}
        />
      )}
    </div>
  );
};

export default React.memo(StreamDestinationsPanel);
